import { createReducer, on } from '@ngrx/store'
import { produce } from 'immer'
import { find as _find, flatMap as _flatMap, flow as _flow, last as _last } from 'lodash/fp'

import { commandCenterKey } from '@flowx/designer/app.utils'
import { AiAnalystActions } from '@flowx/designer/modules/ai/agents/analyst/store/analyst.actions'
import { AiDesignerActions } from '@flowx/designer/modules/ai/agents/designer/store/designer.actions'
import {
  AiCodeEditorContextMenuActions,
  AiDeveloperActions,
} from '@flowx/designer/modules/ai/agents/developer/store/developer.actions'
import {
  ContextItem,
  ConversationOutcomeState,
  ConversationOutcomeType,
  ConversationThread,
  ConversationThreadExecutionPlanActionState,
  ConversationThreadMessageState,
  ConversationThreadState,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { AiConversationStreamService } from '@flowx/designer/modules/ai/conversation/ai-conversation-stream.service'
import {
  ActionProgressTypeToOutcomeClassificationMap,
  AiConversationModel,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import {
  AiActions,
  AiConversationServiceActions,
  CtaActions,
  FloatingWindowActions,
  KeyboardActions,
} from '@flowx/designer/modules/ai/store/ai.actions'
import { uploadedDocumentIdsKey } from '@flowx/designer/modules/ai/store/ai.constants'
import {
  UiDesignerConfigPanelActions,
  UiDesignerExpressionModalActions,
} from '@flowx/designer/modules/ui-designer/ui-designer.actions'

import { PanelType } from './ai-conversation.commons'
import { ConversationErrorActions } from './conversation-error.actions'
import { ActionEditActions, ConversationActions, CtaComponentActions } from './conversation.actions'
import { getConversationMapKey } from './conversation.constants'
import type { ConversationTypes } from './conversation.types'
import { isFinishedThreadState, messageHasValidExecutionPlan } from './conversation.utils'
import { ExecutionPlanActions } from './execution-plan.actions'

export {
  AddDefaultThreadMessageStatus,
  ConversationBusinessRuleTarget,
  ConversationExpressionTarget,
  ConversationTypes,
  ConversationTarget,
  ConversationUiDesignerTarget,
  KeepDiscardOutcomeState,
} from './conversation.types'

export const conversationProcessMapKey = 'conversationProcessMap'

// `conversationStatus` is global, so on its own it only says "some conversation is streaming".
// Stream events carry their `conversationId`, which narrows it to the right one.
const isStreamingConversation = (
  state: Pick<ConversationTypes, 'conversationStatus' | 'latestStreamMessage'>,
  conversationId: string | undefined
): boolean =>
  !!conversationId &&
  state.conversationStatus === 'streaming' &&
  state.latestStreamMessage?.conversationId === conversationId

const initialState: ConversationTypes = {
  commandCenterActivated: localStorage.getItem(commandCenterKey) === 'true',
  conversations: [],
  selectedProcessId: null,
  conversationMapKey: getConversationMapKey({ workspaceId: null, appId: null }, null),
  conversationProcessMap: localStorage.getItem(conversationProcessMapKey)
    ? JSON.parse(localStorage.getItem(conversationProcessMapKey) || '{}')
    : {},
  developerContext: {
    code: null,
    language: null,
    warnings: [],
  },
  target: null,
  conversationStatus: 'idle',
  latestStreamMessage: null,
  pendingMessage: null,
  aiGeneratedNodes: [],
  selectedDocument: null,
  newDocumentsCount: 0,
  uploadedDocumentIds: localStorage.getItem(uploadedDocumentIdsKey)
    ? JSON.parse(localStorage.getItem(uploadedDocumentIdsKey) || '[]')
    : [],
  fileUploadConfig: null,
  uploadedFiles: [],
  panelStack: localStorage.getItem(commandCenterKey) === 'true' ? [PanelType.COMMAND_CENTER] : [],
  keepDiscardOutcomeState: 'idle',
  addDefaultThreadMessageStatus: 'idle',
  assigningPagesToUserTasks: false,
}

export const conversationReducer = createReducer(
  initialState,
  on(AiActions.fabClick, (state) =>
    produce(state, (draft) => {
      draft.commandCenterActivated = true
      draft.panelStack = [PanelType.COMMAND_CENTER]
    })
  ),
  on(ConversationActions.processNavigated, (state, { processResourceId, workspaceId, appId }) =>
    produce(state, (draft) => {
      const conversationMapKey = getConversationMapKey({ workspaceId, appId }, processResourceId)
      const scopeChanged = conversationMapKey !== draft.conversationMapKey

      draft.selectedProcessId = processResourceId
      draft.conversationMapKey = conversationMapKey

      const incomingConversationId = draft.conversationProcessMap[conversationMapKey]

      if (scopeChanged && !isStreamingConversation(state, incomingConversationId)) {
        draft.conversationStatus = 'idle'
        draft.pendingMessage = null
        draft.uploadedFiles = []
        draft.keepDiscardOutcomeState = 'idle'
        draft.selectedDocument = null
      }
    })
  ),
  on(KeyboardActions.escape, (state) =>
    produce(state, (draft) => {
      if (draft.panelStack.length > 0) {
        const topPanel = draft.panelStack[draft.panelStack.length - 1]

        switch (topPanel) {
          case PanelType.DOCUMENT_RESOURCES:
            if (topPanel === PanelType.DOCUMENT_RESOURCES) {
              draft.panelStack.pop()
            }
            break
          case PanelType.DOCUMENT_CONTEXT:
            if (topPanel === PanelType.DOCUMENT_CONTEXT) {
              draft.panelStack.pop()
            }
            break
          case PanelType.COMMAND_CENTER:
            if (topPanel === PanelType.COMMAND_CENTER) {
              draft.commandCenterActivated = false
              draft.panelStack = []
            }
            break
        }
      }
    })
  ),
  on(FloatingWindowActions.minimizeClick, (state) =>
    produce(state, (draft) => {
      draft.commandCenterActivated = false
      draft.panelStack = []
    })
  ),
  on(ConversationActions.createConversationApiResponse, (state, { conversation }) =>
    produce(state, (draft) => {
      draft.conversations.push(conversation)
      draft.conversationProcessMap[draft.conversationMapKey] = conversation.id

      // Set all initial messages to Finished state
      if (conversation.threads) {
        conversation.threads.forEach((thread) => {
          if (thread.messages) {
            thread.messages.forEach((message) => {
              if (!message.state) {
                message.state = ConversationThreadMessageState.Finished
              }
            })
          }
        })
      }
    })
  ),
  on(ConversationActions.createThreadApiResponse, (state, { thread }) =>
    produce(state, (draft) => {
      // Set all messages in the new thread to Finished state
      if (thread.messages) {
        thread.messages.forEach((message) => {
          if (!message.state) {
            message.state = ConversationThreadMessageState.Finished
          }
        })
      }
      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === draft.conversationProcessMap[state.conversationMapKey]
      )
      activeConversation?.threads.push(thread)
    })
  ),
  on(
    ConversationActions.patchMessageContextItemsApiResponse,
    (state, { threadId, messageId, context }) =>
      produce(state, (draft) => {
        const activeConversation = draft.conversations.find(
          (conversation) =>
            conversation.id === draft.conversationProcessMap[state.conversationMapKey]
        )
        const thread = activeConversation?.threads.find((thread: any) => thread.id === threadId)

        if (!thread) {
          console.error(`Thread with id ${threadId} not found`)
          return
        }

        const message = thread.messages?.find((msg: any) => msg.id === messageId)

        if (!message) {
          console.error(`Message with id ${messageId} not found in thread ${threadId}`)
          return
        }

        message.contextData.items = context.items as unknown as ContextItem[]
        // const targetItemIndex = thread.contextData.items.findIndex(
        //   (item: any) => item.key === 'TARGET'
        // )
        // if (targetItemIndex !== -1) {
        //   thread.contextData.items[targetItemIndex] = context.items[0]
        // } else {
        //   thread.contextData.items.push(context.items[0])
        // }

        // const defaultAgentItemIndex = thread.contextData.items.findIndex(
        //   (item: any) => item.key === 'DEFAULT_AGENT'
        // )
        // if (defaultAgentItemIndex !== -1) {
        //   thread.contextData.items[defaultAgentItemIndex] = context.items[1]
        // } else {
        //   thread.contextData.items.push(context.items[1])
        // }
      })
  ),
  on(
    ConversationActions.addThreadMessageApiResponse,
    ExecutionPlanActions.pendingExecutionPlanChangesAfterKeepSavedToNewMessage,
    (state, { threadMessage, threadId }) =>
      produce(state, (draft) => {
        const activeConversation = draft.conversations.find(
          (conversation) =>
            conversation.id === draft.conversationProcessMap[state.conversationMapKey]
        )
        const targetThread = activeConversation?.threads.find(
          (thread: any) => thread.id === threadId
        )

        if (targetThread) {
          targetThread.messages.push(threadMessage)
        }
      })
  ),
  on(ConversationActions.updateMessageContextData, (state, { messageId, contextData }) =>
    produce(state, (draft) => {
      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === draft.conversationProcessMap[state.conversationMapKey]
      )
      const activeThread = activeConversation?.threads.find(
        (thread) => !isFinishedThreadState(thread.state)
      )
      const message = activeThread?.messages.find((message) => message.id === messageId)

      if (message) {
        message.contextData.items = [...message.contextData.items, ...contextData]
      }
    })
  ),
  on(ActionEditActions.businessRuleUpdate, (state, { code, language }) =>
    produce(state, (draft) => {
      draft.developerContext.code = code
      draft.developerContext.language = language
    })
  ),
  on(
    ActionEditActions.generateBusinessRuleCtaClick,
    ActionEditActions.explainBusinessRuleCtaClick,
    ActionEditActions.fixBusinessRuleWarningsCtaClick,
    AiCodeEditorContextMenuActions.addCommentOptionClick,
    AiCodeEditorContextMenuActions.improveCodeOptionClick,
    UiDesignerExpressionModalActions.generateClick,
    UiDesignerConfigPanelActions.expressionGenerateClick,
    CtaActions.explainBusinessRuleCtaClick,
    CtaActions.fixBusinessRuleWarningsCtaClick,
    CtaComponentActions.generateUiCtaClick,
    AiDeveloperActions.nodeConfigChatOpened,
    (state) =>
      produce(state, (draft) => {
        draft.commandCenterActivated = true
        draft.panelStack = [PanelType.COMMAND_CENTER]
      })
  ),
  on(AiConversationServiceActions.conversationStarted, (state) =>
    produce(state, (draft) => {
      draft.conversationStatus = 'streaming'
    })
  ),
  on(AiConversationServiceActions.conversationCreating, (state) =>
    produce(state, (draft) => {
      draft.conversationStatus = 'streaming'
    })
  ),
  on(AiConversationServiceActions.executionPlanActionListenStarted, (state) =>
    produce(state, (draft) => {
      draft.conversationStatus = 'streaming'
    })
  ),
  on(ConversationActions.conversationStreamingStarted, (state, { messageId }) =>
    produce(state, (draft) => {
      draft.conversationStatus = 'streaming'
      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === draft.conversationProcessMap[state.conversationMapKey]
      )
      const message = activeConversation?.threads
        .flatMap((thread) => thread.messages)
        .find((message) => message.id === messageId)

      if (message) {
        message.state = ConversationThreadMessageState.Processing
      }
    })
  ),
  on(AiConversationServiceActions.conversationCreated, (state) =>
    produce(state, (draft) => {
      draft.conversationStatus = 'idle'
    })
  ),
  on(AiConversationServiceActions.conversationMessageStreamError, (state) =>
    produce(state, (draft) => {
      draft.conversationStatus = 'error'
    })
  ),
  on(AiConversationServiceActions.conversationMessageStreamEnded, (state) =>
    produce(state, (draft) => {
      draft.conversationStatus = 'finished'
      // Note: The complete thread with all server-generated fields (including outcome IDs)
      // will be loaded via the threadMessageFetchSuccess action that follows this one
    })
  ),
  on(ConversationActions.reloadMessageSuccess, (state, { message, threadId }) =>
    produce(state, (draft) => {
      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === draft.conversationProcessMap[state.conversationMapKey]
      )

      if (!activeConversation) {
        console.error('Active conversation not found')
        return
      }

      const thread = activeConversation.threads.find((t) => t.id === threadId)

      if (!thread) {
        console.error(`Thread ${threadId} not found`)
        return
      }

      const messageIndex = thread.messages.findIndex((m) => m.id === message.id)

      if (messageIndex < 0) {
        console.error(`Message ${message.id} not found in thread ${threadId}`)
        return
      }

      // Complete replacement - backend is source of truth
      thread.messages[messageIndex] = message
    })
  ),
  on(AiConversationServiceActions.aiConversationStreamUpdate, (state, { updates }) =>
    produce(state, (draft) => {
      draft.conversationStatus = 'streaming'
      draft.latestStreamMessage = updates

      // Find the message in any thread based on the messageId from updates
      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === draft.conversationProcessMap[state.conversationMapKey]
      )
      const messageWithThread = _flow(
        _flatMap((thread: ConversationThread) =>
          thread.messages.map((message) => ({ thread, message }))
        ),
        _find(({ message }) => message?.id === updates.messageId)
      )(activeConversation?.threads)
      const message = messageWithThread?.message
      const activeThread = messageWithThread?.thread

      if (!message) {
        return
      }

      message.state = ConversationThreadMessageState.Processing

      const content = AiConversationStreamService.getProgressContent(updates)
      if (content === null) {
        return
      }

      message.outcome ??= { outcomes: [] }

      // Each event carries the full cumulative snapshot for its progress type
      // — replace the textual outcome of this classification in place (or
      // create it on the first event). No accumulation across events.
      const classification = ActionProgressTypeToOutcomeClassificationMap[updates.progressType]
      const existing = message.outcome.outcomes.find(
        (outcome) =>
          outcome.outcomeType === ConversationOutcomeType.Textual &&
          outcome.outcomeClassification === classification
      )

      if (existing) {
        existing.textual.content = content
      } else {
        message.outcome.outcomes.push({
          state: ConversationOutcomeState.Created,
          outcomeType: ConversationOutcomeType.Textual,
          outcomeClassification: classification,
          textual: { content },
        })
      }

      activeThread.outcome = AiConversationModel.mapMessageOutcomeToThreadOutcome(updates, message)
    })
  ),
  on(ConversationActions.keepOutcomeClick, (state) =>
    produce(state, (draft) => {
      draft.keepDiscardOutcomeState = 'keeping'
    })
  ),
  on(ConversationActions.discardOutcomeClick, (state) =>
    produce(state, (draft) => {
      draft.keepDiscardOutcomeState = 'discarding'
    })
  ),
  on(ConversationActions.keepThreadApiResponse, (state, { conversationId, threadId }) =>
    produce(state, (draft) => {
      draft.keepDiscardOutcomeState = 'idle'
      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === conversationId
      )
      if (!activeConversation) {
        return
      }

      const thread = activeConversation.threads.find((thread) => thread.id === threadId)
      if (!thread) {
        return
      }
      thread.state = ConversationThreadState.Kept

      const actionId = thread.messages[0].executionPlan?.actions[0]?.localId
      if (!actionId) {
        return
      }

      const defaultThread = activeConversation.threads.find(
        (thread) => thread.id === activeConversation.defaultThreadId
      )

      if (!defaultThread) {
        return
      }

      const validMessage = _last(defaultThread.messages.filter(messageHasValidExecutionPlan))
      const action = validMessage?.executionPlan.actions.find(
        (action) => action.localId === actionId
      )
      if (!action) {
        return
      }

      action.state = ConversationThreadExecutionPlanActionState.Finished
    })
  ),
  on(ConversationActions.discardThreadApiResponse, (state, { conversationId, threadId }) =>
    produce(state, (draft) => {
      draft.keepDiscardOutcomeState = 'idle'
      draft.addDefaultThreadMessageStatus = 'idle'
      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === conversationId
      )
      if (!activeConversation) {
        return
      }

      const thread = activeConversation.threads.find((thread) => thread.id === threadId)
      if (!thread) {
        return
      }
      thread.state = ConversationThreadState.Discarded

      const actionId = thread.messages[0].executionPlan?.actions[0]?.localId
      if (!actionId) {
        return
      }

      const defaultThread = activeConversation.threads.find(
        (thread) => thread.id === activeConversation.defaultThreadId
      )

      if (!defaultThread) {
        return
      }

      const validMessage = _last(defaultThread.messages.filter(messageHasValidExecutionPlan))
      const action = validMessage?.executionPlan.actions.find(
        (action) => action.localId === actionId
      )

      if (!action) {
        return
      }

      action.state = ConversationThreadExecutionPlanActionState.Pending
    })
  ),
  on(ConversationActions.recoverThreadAfterError, (state, { threadId }) =>
    produce(state, (draft) => {
      draft.keepDiscardOutcomeState = 'idle'
      draft.addDefaultThreadMessageStatus = 'idle'

      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === draft.conversationProcessMap[state.conversationMapKey]
      )
      const actionId = activeConversation?.threads.find((thread) => thread.id === threadId)
        ?.messages[0].executionPlan?.actions[0]?.localId
      const defaultThread = activeConversation?.threads.find(
        (thread) => thread.id === activeConversation.defaultThreadId
      )
      const validMessage = _last(defaultThread?.messages.filter(messageHasValidExecutionPlan) ?? [])
      const action = actionId
        ? validMessage?.executionPlan.actions.find((action) => action.localId === actionId)
        : undefined

      if (action) {
        action.state = ConversationThreadExecutionPlanActionState.Pending
      }
    })
  ),
  on(
    ExecutionPlanActions.patchExecutionPlanActionApiResponse,
    (state, { thread, conversationId }) =>
      produce(state, (draft) => {
        const activeConversation = draft.conversations.find(
          (conversation) =>
            conversation.id === draft.conversationProcessMap[state.conversationMapKey]
        )
        if (activeConversation && activeConversation.id === conversationId) {
          activeConversation.threads.push(thread)
        }
      })
  ),
  on(CtaComponentActions.generateProcessCtaClick, (state) =>
    produce(state, (draft) => {
      draft.commandCenterActivated = true
      draft.panelStack = [PanelType.COMMAND_CENTER]
    })
  ),
  on(AiActions.enterCommandCenter, (state) =>
    produce(state, (draft) => {
      draft.commandCenterActivated = true
      draft.panelStack = [PanelType.COMMAND_CENTER]
    })
  ),
  on(AiActions.exitCommandCenter, (state) =>
    produce(state, (draft) => {
      draft.commandCenterActivated = false
      draft.panelStack = []
    })
  ),
  on(FloatingWindowActions.minimizeClick, AiActions.outsideOfAiChatClick, (state) =>
    produce(state, (draft) => {
      draft.commandCenterActivated = false
      draft.panelStack = []
    })
  ),
  on(ConversationActions.clearActiveConversation, (state) =>
    produce(state, (draft) => {
      draft.conversations = draft.conversations.filter(
        (conversation) => conversation.id !== draft.conversationProcessMap[state.conversationMapKey]
      )
      delete draft.conversationProcessMap[state.conversationMapKey]
      draft.selectedDocument = null
      draft.keepDiscardOutcomeState = 'idle'
    })
  ),
  on(ExecutionPlanActions.deleteExecutionPlan, (state) =>
    produce(state, (draft) => {
      draft.conversations = draft.conversations.filter(
        (conversation) => conversation.id !== draft.conversationProcessMap[state.conversationMapKey]
      )
      delete draft.conversationProcessMap[state.conversationMapKey]
      draft.conversationStatus = 'idle'
      draft.latestStreamMessage = null
      draft.keepDiscardOutcomeState = 'idle'
    })
  ),
  on(ExecutionPlanActions.patchExecutionPlanAction, (state, { actionId, actionUpdate }) =>
    produce(state, (draft) => {
      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === draft.conversationProcessMap[state.conversationMapKey]
      )
      if (!activeConversation) {
        return
      }

      const defaultThread = activeConversation.threads.find(
        (thread) => thread.id === activeConversation.defaultThreadId
      )

      if (!defaultThread) {
        return
      }

      const validMessage = _last(defaultThread.messages.filter(messageHasValidExecutionPlan))

      validMessage.executionPlan.actions = validMessage.executionPlan.actions.map((action) => {
        if (action.localId === actionId) {
          return {
            ...action,
            ...actionUpdate,
          }
        }
        return action
      })
    })
  ),
  on(
    ExecutionPlanActions.patchExecutionPlanActionState,
    (state, { actionId, actionState, messageId }) =>
      produce(state, (draft) => {
        const activeConversation = draft.conversations.find(
          (conversation) =>
            conversation.id === draft.conversationProcessMap[state.conversationMapKey]
        )
        if (!activeConversation) {
          return
        }

        const defaultThread = activeConversation.threads.find(
          (thread) => thread.id === activeConversation.defaultThreadId
        )

        if (!defaultThread) {
          return
        }

        // Find the specific message by ID
        const message = defaultThread.messages.find((msg) => msg.id === messageId)
        if (!message) {
          return
        }

        // Find the action within that message's execution plan
        const action = message.executionPlan.actions.find((action) => action.localId === actionId)
        if (!action) {
          return
        }

        if (actionState === ConversationThreadExecutionPlanActionState.Executing) {
          // Reset all Executing actions to Pending within this message's execution plan
          message.executionPlan.actions.forEach((action) => {
            if (action.state === ConversationThreadExecutionPlanActionState.Executing) {
              action.state = ConversationThreadExecutionPlanActionState.Pending
            }
          })
        }

        action.state = actionState
      })
  ),
  on(ExecutionPlanActions.patchExecutionPlanActionTargetSuccess, (state, { actionId, target }) =>
    produce(state, (draft) => {
      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === draft.conversationProcessMap[state.conversationMapKey]
      )
      if (!activeConversation) {
        return
      }

      const defaultThread = activeConversation.threads.find(
        (thread) => thread.id === activeConversation.defaultThreadId
      )

      if (!defaultThread) {
        return
      }

      const validMessage = _last(defaultThread.messages.filter(messageHasValidExecutionPlan))
      const action = validMessage?.executionPlan.actions.find(
        (action) => action.localId === actionId
      )
      if (!action) {
        return
      }

      action.target = target
    })
  ),
  on(ConversationActions.updatePendingMessage, (state, { message }) =>
    produce(state, (draft) => {
      draft.pendingMessage = message
    })
  ),
  on(ConversationActions.conversationCreationCancelled, (state) =>
    produce(state, (draft) => {
      draft.pendingMessage = null
      draft.conversationStatus = 'idle'
      draft.selectedDocument = null

      draft.conversations = draft.conversations.filter(
        (conversation) => conversation.id !== draft.conversationProcessMap[state.conversationMapKey]
      )
      delete draft.conversationProcessMap[state.conversationMapKey]
    })
  ),
  on(ConversationActions.defaultThreadMessageCancelled, (state) =>
    produce(state, (draft) => {
      draft.pendingMessage = null
      draft.conversationStatus = 'idle'
      draft.addDefaultThreadMessageStatus = 'idle'
    })
  ),
  on(AiAnalystActions.keepOutcomeSuccess, (state, { response }) =>
    produce(state, (draft) => {
      draft.aiGeneratedNodes = response.nodes
    })
  ),
  on(ConversationActions.documentContextOpened, (state) =>
    produce(state, (draft) => {
      draft.newDocumentsCount = 0
      draft.panelStack.push(PanelType.DOCUMENT_CONTEXT)
    })
  ),
  on(ConversationActions.documentContextClosed, (state) =>
    produce(state, (draft) => {
      draft.newDocumentsCount = 0
      draft.panelStack = draft.panelStack.filter(
        (p) => ![PanelType.DOCUMENT_CONTEXT, PanelType.DOCUMENT_RESOURCES].includes(p)
      )
    })
  ),
  on(ConversationActions.documentSelected, (state, { document }) =>
    produce(state, (draft) => {
      draft.selectedDocument = document
      draft.panelStack = draft.panelStack.filter(
        (p) => ![PanelType.DOCUMENT_CONTEXT, PanelType.DOCUMENT_RESOURCES].includes(p)
      )
    })
  ),
  on(ConversationActions.documentResourcesOpened, (state) =>
    produce(state, (draft) => {
      draft.panelStack.push(PanelType.DOCUMENT_RESOURCES)
    })
  ),
  on(ConversationActions.documentResourcesClosed, (state) =>
    produce(state, (draft) => {
      const topPanel = draft.panelStack[draft.panelStack.length - 1]
      if (topPanel === PanelType.DOCUMENT_RESOURCES) {
        draft.panelStack.pop()
      }
    })
  ),
  on(ConversationActions.documentReady, (state, { documentId }) =>
    produce(state, (draft) => {
      draft.newDocumentsCount++
      draft.uploadedDocumentIds = draft.uploadedDocumentIds.filter((d) => d !== documentId)
      localStorage.setItem(uploadedDocumentIdsKey, JSON.stringify(draft.uploadedDocumentIds))
    })
  ),
  on(ConversationActions.documentUploaded, (state, { documentId }) =>
    produce(state, (draft) => {
      draft.uploadedDocumentIds.push(documentId)
      localStorage.setItem(uploadedDocumentIdsKey, JSON.stringify(draft.uploadedDocumentIds))
    })
  ),
  on(ConversationActions.loadFileUploadConfigSuccess, (state, { fileUploadConfig }) =>
    produce(state, (draft) => {
      draft.fileUploadConfig = fileUploadConfig
    })
  ),
  on(ConversationActions.uploadedFilesUpdated, (state, { files }) =>
    produce(state, (draft) => {
      draft.uploadedFiles = files
    })
  ),
  on(ConversationActions.fileRemoveClick, (state, { id }) =>
    produce(state, (draft) => {
      draft.uploadedFiles = state.uploadedFiles.filter((file) => file.id !== id)
    })
  ),
  on(ConversationActions.clearUploadedFiles, (state) =>
    produce(state, (draft) => {
      draft.uploadedFiles = []
    })
  ),
  on(ConversationActions.threadMessageFetchSuccess, (state, { thread }) =>
    produce(state, (draft) => {
      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === draft.conversationProcessMap[state.conversationMapKey]
      )
      if (thread && activeConversation) {
        const threadIndex = activeConversation.threads.findIndex(
          (existingThread) => existingThread.id === thread.id
        )

        if (threadIndex !== -1) {
          // Replace the existing thread with the fetched thread
          activeConversation.threads[threadIndex] = thread
        }
      }
    })
  ),
  on(ConversationActions.updateThreadOutcome, (state, { threadId, outcome }) =>
    produce(state, (draft) => {
      const activeConversation = draft.conversations.find(
        (conversation) => conversation.id === draft.conversationProcessMap[state.conversationMapKey]
      )
      const thread = activeConversation?.threads.find((t) => t.id === threadId)
      if (thread) {
        thread.outcome = outcome
      }
    })
  ),
  on(ConversationActions.addDefaultThreadMessageStarted, (state) =>
    produce(state, (draft) => {
      draft.addDefaultThreadMessageStatus = 'loading'
    })
  ),
  on(ConversationActions.addDefaultThreadMessageSuccess, (state) =>
    produce(state, (draft) => {
      draft.addDefaultThreadMessageStatus = 'idle'
    })
  ),
  on(ConversationActions.addDefaultThreadMessageError, (state) =>
    produce(state, (draft) => {
      draft.addDefaultThreadMessageStatus = 'error'
    })
  ),
  on(ConversationActions.loadConversationSuccess, (state, { conversation }) =>
    produce(state, (draft) => {
      const loaded = {
        ...conversation,
        threads: (conversation.threads || []).map((thread) => ({
          ...thread,
          messages: (thread.messages || []).map((message) => ({
            ...message,
            state: message.state || ConversationThreadMessageState.Finished,
          })),
        })),
      }

      // Replace rather than skip: results that landed while this conversation was out of scope were
      // never written to the in-memory copy, so a stale entry would survive the refetch that exists
      // to repair it. A conversation that is mid-stream is the exception — its in-memory copy is
      // ahead of the server, and overwriting it leaves the following delta chunks with no message
      // to apply to.
      const exists = draft.conversations.some((conv) => conv.id === conversation.id)
      const keepInMemoryCopy = exists && isStreamingConversation(state, conversation.id)

      draft.conversations = keepInMemoryCopy
        ? draft.conversations
        : exists
          ? draft.conversations.map((conv) => (conv.id === conversation.id ? loaded : conv))
          : [...draft.conversations, loaded]
    })
  ),
  on(
    ConversationErrorActions.keepOutcomeFailed,
    AiDesignerActions.saveDataModelDraftFailure,
    AiDesignerActions.linkTemplateConfigKeysFailure,
    (state) =>
      produce(state, (draft) => {
        draft.keepDiscardOutcomeState = 'idle'
      })
  ),
  on(AiAnalystActions.beginAssigningPagesToUserTasks, (state) =>
    produce(state, (draft) => {
      draft.assigningPagesToUserTasks = true
    })
  ),
  on(
    AiAnalystActions.assignPagesToUserTasksSuccess,
    AiAnalystActions.assignPagesToUserTasksFailed,
    (state) =>
      produce(state, (draft) => {
        draft.assigningPagesToUserTasks = false
      })
  ),
  on(
    ConversationActions.conversationClosedSuccess,
    ConversationActions.conversationClosedFailure,
    ConversationErrorActions.conversationEvicted,
    (state, { conversationId }) =>
      produce(state, (draft) => {
        draft.conversations = draft.conversations.filter(
          (conversation) => conversation.id !== conversationId
        )
        draft.conversationStatus = 'idle'
        draft.pendingMessage = null
        draft.uploadedFiles = []
        draft.keepDiscardOutcomeState = 'idle'
        draft.addDefaultThreadMessageStatus = 'idle'
        draft.assigningPagesToUserTasks = false
        const processResourceId = Object.keys(draft.conversationProcessMap).find(
          (key) => draft.conversationProcessMap[key] === conversationId
        )
        if (processResourceId) {
          delete draft.conversationProcessMap[processResourceId]
        }
      })
  )
)
